/**
 * Разблокировка звука. Вызывать СИНХРОННО в начале обработчика нажатия — до любых await.
 *
 * 1. Safari и WebView Telegram разрешают AudioContext.resume() только внутри жеста.
 *    Если сначала дождаться сети, а потом resume() — контекст останется suspended, звука не будет.
 * 2. На iPhone Web Audio молчит при включённом беззвучном режиме (переключатель сбоку).
 *    navigator.audioSession.type = "playback" (iOS 16.4+) переводит страницу в режим плеера,
 *    как у музыкальных приложений. Для старых iOS — тихий <audio>-элемент делает то же самое.
 * 3. Но в режиме "playback" Safari запрещает микрофон (InvalidStateError: AudioSession category is not
 *    compatible with audio capture). На время записи голоса — beginCapture()/endCapture().
 */
import { audioContext, markAudioContextStale } from "./engine";

interface AudioSessionNav {
  audioSession?: { type: string };
}

let silent: HTMLAudioElement | null = null;
let capturing = false;

function setSessionType(type: string): void {
  const nav = navigator as Navigator & AudioSessionNav;
  try {
    if (nav.audioSession) nav.audioSession.type = type;
  } catch {
    /* старый браузер */
  }
}

/** Перед getUserMedia: режим «играть и записывать», иначе Safari не даст микрофон */
export function beginCapture(type: "play-and-record" | "auto" = "play-and-record"): void {
  capturing = true;
  setSessionType(type);
}

/** После записи: обратно в режим плеера (звук и при беззвучном режиме iPhone) */
export function endCapture(): void {
  if (!capturing) return;
  capturing = false;
  setSessionType("playback");
  markAudioContextStale();
}

function silentWavUrl(): string {
  const rate = 8000;
  const samples = rate / 2;
  const buf = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF");
  v.setUint32(4, 36 + samples * 2, true);
  str(8, "WAVE");
  str(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, "data");
  v.setUint32(40, samples * 2, true);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
}

export function unlockAudio(): void {
  const nav = navigator as Navigator & AudioSessionNav;
  if (!capturing) setSessionType("playback");
  const ctx = audioContext();
  if (ctx.state !== "running") void ctx.resume().catch(() => {});

  if (!nav.audioSession && /iPhone|iPad|iPod/.test(navigator.userAgent) && !silent) {
    silent = new Audio(silentWavUrl());
    silent.loop = true;
    silent.setAttribute("playsinline", "");
    void silent.play().catch(() => {
      silent = null;
    });
  }
}

/** Разблокируем на первом же касании где угодно — к моменту «Слушать» контекст уже работает */
export function installAudioUnlock(): void {
  const handler = () => {
    unlockAudio();
    if (audioContext().state === "running") {
      for (const e of ["pointerdown", "touchend", "keydown"]) document.removeEventListener(e, handler, true);
    }
  };
  for (const e of ["pointerdown", "touchend", "keydown"]) document.addEventListener(e, handler, true);
}
